import { GridColDef, GridComparatorFn } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TachographDriverRow } from "../models/Tachograph";
import { dueRowClassName, DUE_ROW_SX, TACHOGRAPH_PAGE_SIZES } from "./tachographGridDefaults";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";
import { LoadFailedOverlay } from "@/components/grid/LoadFailedOverlay";
import { NoRowsOverlay } from "@/components/grid/NoRowsOverlay";
import { formatDate } from "@/hooks/metrics/useFormattedDate";

type Props = {
  rows: TachographDriverRow[];
  loading: boolean;
  /** True while a source of the rows is loading or failed. */
  exportDisabled: boolean;
  /** True when a source of the rows failed: an empty grid then says so instead of "no rows". */
  loadFailed: boolean;
  /** Colours overdue rows red and rows due soon yellow. */
  highlightRows: boolean;
};

/** Drivers without a readout are the most overdue, so they sort ahead of every dated row. */
const compareDueDates: GridComparatorFn<Date | null> = (a, b, cellA, cellB) => {
  const noReadoutA = (cellA.api.getRow(cellA.id) as TachographDriverRow | undefined)?.noReadout ?? false;
  const noReadoutB = (cellB.api.getRow(cellB.id) as TachographDriverRow | undefined)?.noReadout ?? false;
  if (noReadoutA || noReadoutB) return Number(noReadoutB) - Number(noReadoutA);

  return (a?.getTime() ?? 0) - (b?.getTime() ?? 0);
};

/** One row per driver with the deadline for the driver card. */
export function TachographDriverGrid({ rows, loading, exportDisabled, loadFailed, highlightRows }: Props) {
  const { t, i18n } = useTranslation();

  const columns = useMemo<GridColDef<TachographDriverRow>[]>(
    () => [
      { field: "driver", headerName: t("tachograph.columns.driver"), minWidth: 260, flex: 1 },
      {
        field: "driverCardNumber",
        headerName: t("tachograph.columns.driverCardNumber"),
        minWidth: 220,
        flex: 1,
      },
      {
        field: "licensePlate",
        headerName: t("tachograph.columns.licensePlate"),
        minWidth: 180,
        flex: 0.8,
      },
      {
        field: "legalLimit",
        headerName: t("tachograph.columns.legalLimit"),
        minWidth: 220,
        flex: 1,
        type: "date",
        valueGetter: (_value, row) => row.dueDate,
        sortComparator: compareDueDates,
        renderCell: params => params.row.legalLimit,
      },
      {
        field: "nextDownloadUtc",
        headerName: t("tachograph.columns.nextDownloadUtc"),
        minWidth: 220,
        flex: 1,
        type: "date",
        valueGetter: (_value, row) => row.dueDate,
        sortComparator: compareDueDates,
        renderCell: params => params.row.nextDownloadUtc,
      },
      {
        field: "updateTime",
        headerName: t("tachograph.columns.updateTime"),
        width: 170,
        type: "date",
        valueGetter: (_value, row) => row.updateDate,
        valueFormatter: (value: Date | null) => formatDate(value ?? undefined, i18n.language),
      },
    ],
    [t, i18n.language],
  );

  return (
    <I18nGrid
      rows={rows}
      columns={columns}
      loading={loading}
      pagination
      disableRowSelectionOnClick
      pageSizeOptions={TACHOGRAPH_PAGE_SIZES}
      slots={{
        noRowsOverlay: loadFailed ? LoadFailedOverlay : NoRowsOverlay,
        toolbar: ExportToolbar,
      }}
      slotProps={{ toolbar: { report: { title: t("tachograph.tabs.driver") }, exportDisabled } }}
      getRowClassName={highlightRows ? params => dueRowClassName(params.row.downloadStatus) : undefined}
      sx={DUE_ROW_SX}
      settingsKey="tachographDrivers"
      initialState={{ density: "compact", sorting: { sortModel: [{ field: "driver", sort: "asc" }] } }}
    />
  );
}
